import { Badge, Group, Progress, Stack, Text, Tooltip } from "@mantine/core";
import {
  FAILED,
  IN_PROGRESS,
  SKIPPED,
  SUCCEEDED,
  statusColor,
  statusLabel,
  type DeploymentProgress,
} from "@/lib/deployments";

/**
 * One stacked bar out of the deployment's expected store count.
 *
 * The sections are the four statuses; the unfilled remainder is stores that
 * have not reported at all. That means the bar fills left to right as the
 * deploy rolls, and what is left of the track is exactly what is outstanding —
 * no separate "waiting" colour that would make a full bar ambiguous.
 *
 * A blast's denominator is the whole fleet, and a base station that is offline
 * never answers, so the bar is not expected to reach 100% every time. The
 * "N not reporting" count under it is what distinguishes "still rolling" from
 * "as done as it is going to get", which is why it is shown rather than folded
 * into a percentage.
 */
interface DeploymentProgressBarProps {
  progress: DeploymentProgress;
  /** False once the deployment is finished, to stop the stripe animating. */
  live: boolean;
}

const SECTIONS = [SUCCEEDED, FAILED, SKIPPED, IN_PROGRESS] as const;

export function DeploymentProgressBar({
  progress,
  live,
}: DeploymentProgressBarProps) {
  const { counts, expected, reported, finished, silent } = progress;

  const countsByStatus: Record<string, number> = {
    [SUCCEEDED]: counts.succeeded,
    [FAILED]: counts.failed,
    [SKIPPED]: counts.skipped,
    [IN_PROGRESS]: counts.inProgress,
  };
  const countFor = (status: string) => countsByStatus[status] ?? 0;

  // With no known denominator the bar can only show the mix of what has
  // reported, so it is scaled to that instead of to a fleet size it does not
  // have. The caption below says which of the two is being shown.
  const total = expected ?? Math.max(reported, 1);

  const percent =
    progress.fraction === null ? null : Math.round(progress.fraction * 100);

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="xs">
        <Text size="sm" fw={600}>
          {expected === null
            ? `${finished} of ${reported} reporting stores finished`
            : `${finished} of ${expected} stores finished`}
        </Text>
        {percent === null ? null : (
          <Text size="sm" c="dimmed">
            {percent}%
          </Text>
        )}
      </Group>

      <Progress.Root size="xl" radius="sm">
        {SECTIONS.map((status) => {
          const count = countFor(status);
          if (count === 0) return null;
          return (
            <Tooltip
              key={status}
              label={`${count} ${statusLabel(status).toLowerCase()}`}
            >
              <Progress.Section
                value={(count / total) * 100}
                color={statusColor(status)}
                // Only the in-flight section animates, and only while the
                // deployment is actually running — a finished deploy left on
                // screen should look still.
                striped={status === IN_PROGRESS}
                animated={status === IN_PROGRESS && live}
              />
            </Tooltip>
          );
        })}
      </Progress.Root>

      <Group gap="xs" wrap="wrap">
        {SECTIONS.map((status) => (
          <Badge
            key={status}
            color={statusColor(status)}
            variant={countFor(status) ? "filled" : "light"}
            size="sm"
          >
            {countFor(status)} {statusLabel(status).toLowerCase()}
          </Badge>
        ))}
        {counts.other > 0 ? (
          <Badge color="gray" size="sm">
            {counts.other} other
          </Badge>
        ) : null}
        {silent ? (
          <Badge color="gray" variant="outline" size="sm">
            {silent} not reporting
          </Badge>
        ) : null}
      </Group>

      {expected === null ? (
        <Text size="xs" c="dimmed">
          Store inventory unavailable, so the size of the fleet this blast
          addresses is unknown — the bar shows only the stores that have
          reported.
        </Text>
      ) : null}
    </Stack>
  );
}
